import React from "react";
import { isInternalLink } from "./linkUtils";

const IMAGE_DOMAIN = "https://media.dinamani.com/";

// Image metadata type for focus-point support
export type ImageMetadata = {
  'focus-point'?: [number, number];
  width?: number;
  height?: number;
};

// Check if focus-point exists in image metadata
export function hasFocusPoint(metadata?: ImageMetadata): boolean {
  return !!(metadata?.['focus-point'] && metadata.width && metadata.height);
}

// Calculate focus point as percentage for object-position
// If focus-point exists, returns { objectPosition: "X% Y%" }
// If not, returns { objectPosition: 'top' } as default
export function getFocusPointStyle(metadata?: ImageMetadata): React.CSSProperties {
  if (!hasFocusPoint(metadata)) {
    return { objectPosition: 'top' };
  }
  const [focusX, focusY] = metadata!['focus-point']!;
  const xPercent = Math.round((focusX / metadata!.width!) * 100);
  const yPercent = Math.round((focusY / metadata!.height!) * 100);
  return { objectPosition: `${xPercent}% ${yPercent}%` };
}

// Tiny 1x1 gray blur placeholder for images (base64 encoded)
// Use this with Next.js Image component: placeholder="blur" blurDataURL={BLUR_PLACEHOLDER}
export const BLUR_PLACEHOLDER = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAn/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBEQCEAwEPwAB//9k=";

// Card field values are typed `string | boolean` (the same FieldConfig shape
// is reused for text fields and boolean flags like isliveblog), but text
// fields like title/excerpt/timeAgo/imageSrc should only ever hold a string.
// `??` doesn't catch a literal `false` (it's not nullish), so consumers that
// need a plain string use this to also discard a stray boolean.
export function toFieldString(value?: string | boolean, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

// Image URL function
export function getImageUrl(value?: string): string {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  return IMAGE_DOMAIN + value;
}


// Tamil month names
const TAMIL_MONTHS = [
  "ஜனவரி",
  "பிப்ரவரி",
  "மார்ச்",
  "ஏப்ரல்",
  "மே",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Time ago function (Tamil format)
export function getTimeAgo(timestamp?: string | number): string {
  if (!timestamp) return "";

  const now = Date.now();
  const numericTimestamp = typeof timestamp === "string" ? Number(timestamp) : timestamp;

  // If the value is already a formatted string (not a valid number), return it as-is
  if (isNaN(numericTimestamp)) return String(timestamp);

  // Detect if timestamp is in seconds (10 digits) or milliseconds (13 digits)
  // Timestamps less than 10^12 (year 2001 in ms) are likely in seconds
  const time = numericTimestamp < 1e12 ? numericTimestamp * 1000 : numericTimestamp;

  const diffMs = now - time;
  if (diffMs < 0) return "";

  const diffSeconds = Math.floor(diffMs / 1000);

  // Under 60 seconds → relative "X வினாடிகள் முன்பு".
  if (diffSeconds < 60) {
    return diffSeconds === 1
      ? "1 வினாடி முன்பு"
      : `${diffSeconds} வினாடிகள் முன்பு`;
  }

  // 60 seconds or more → absolute IST date/time.
  const date = new Date(time);
  // Convert to IST (UTC+5:30)
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + istOffset + date.getTimezoneOffset() * 60 * 1000);
  const day = istDate.getDate();
  const month = TAMIL_MONTHS[istDate.getMonth()];
  const year = istDate.getFullYear();
  let hours = istDate.getHours();
  const minutes = istDate.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm} IST`;
}

// Machine-readable counterpart to getTimeAgo(), for building a semantic
// <time datetime=".." title=".."> element (Google SERP reads this, not the
// Tamil display text) alongside the same Tamil-formatted label.
export function getTimeAgoMeta(
  timestamp?: string | number
): { text: string; iso: string; title: string } | null {
  if (!timestamp) return null;
  const numericTimestamp = typeof timestamp === "string" ? Number(timestamp) : timestamp;
  if (isNaN(numericTimestamp)) return null;

  const time = numericTimestamp < 1e12 ? numericTimestamp * 1000 : numericTimestamp;
  const iso = new Date(time).toISOString();
  return {
    text: getTimeAgo(timestamp),
    iso,
    title: iso.slice(0, 16).replace("T", " "),
  };
}


// Build sticky CSS styles from column config
// When sticky: true, applies position sticky with configurable top offset
// Columns with tabBar return {} here — tab header sticky is controlled by
// stickyTabs: true in JSON config, passed to TabBarSection separately
export function getStickyStyles(config: {
  sticky?: boolean;
  stickyTop?: string;
  hasTabBar?: boolean;
}): React.CSSProperties {
  if (!config.sticky) return {};

  // TabBar columns: no sticky on the wrapper — it moves to TabBarSection's tab header
  if (config.hasTabBar) return {};

  const top = config.stickyTop || '150px';

  // Regular sticky columns: constrain height and allow inner scroll
  return {
    position: 'sticky',
    top,
    alignSelf: 'flex-start',
    zIndex: 40,
    maxHeight: `calc(100vh - ${top} - 20px)`,
    overflowY: 'auto',
  };
}

// Section URL function - extracts path from full URL
// Example: "https://www.dinamani.com/visual-stories" → "/visual-stories"
export function getSectionUrl(url?: string): string {
  if (!url) return "";

  // If already a relative path, return as-is
  if (url.startsWith("/")) return url;

  // If it's a full URL, extract the pathname
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname || "/";
    } catch {
      return "";
    }
  }

  // If it's just a slug without leading slash, add one
  return `/${url}`;
}

/**
 * Get link properties for widget links (image widget, etc.)
 *
 * Determines whether to use Next.js Link (internal navigation) or <a> tag (external).
 * - Internal links (e.g., "/t20worldcup") → useNextLink: true, href for Next.js Link
 * - External links with _blank → useNextLink: false, target="_blank", rel="noopener noreferrer"
 * - External links with _self → useNextLink: false, target="_self"
 *
 * linkTarget from JSON:
 *   "_blank" → open in new tab
 *   "_self"  → open in same tab
 */
export function getWidgetLinkProps(link?: string, linkTarget?: string): {
  useNextLink: boolean;
  href: string;
  target?: string;
  rel?: string;
} | null {
  if (!link) return null;

  const internal = isInternalLink(link);

  // Internal links: always use Next.js Link for client-side navigation
  if (internal) {
    // Ensure internal links start with "/"
    const href = link.startsWith('/') ? link : getSectionUrl(link);
    return {
      useNextLink: true,
      href,
      target: linkTarget === '_blank' ? '_blank' : undefined,
      rel: linkTarget === '_blank' ? 'noopener noreferrer' : undefined,
    };
  }

  // External links
  if (linkTarget === '_blank') {
    return {
      useNextLink: false,
      href: link,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  // External link with _self (open in same tab)
  return {
    useNextLink: false,
    href: link,
    target: '_self',
  };
}
